import { Box, Divider, Typography } from "@mui/material";
import OutlineCard from "../../../../../../components/OutlineCard";
import ProfileViewer from "../../../../../../components/ProfileViewer";
import Text from "../../../../../../components/Text";

interface PatientHippaConsentProps {
  values: any;
}

const PatientHippaConsent = ({ values }: PatientHippaConsentProps) => {
  // Prepare question-answer data
  const questionAnswerData: { [key: string]: string } = {
    "May we phone, email, or send a text to you to confirm appointments?":
      values.appointmentContact === "yes"
        ? "Yes"
        : values.appointmentContact === "no"
          ? "No"
          : "",
    "May we leave a message on your answering machine at home or on your cell phone?":
      values.leaveMessage === "yes"
        ? "Yes"
        : values.leaveMessage === "no"
          ? "No"
          : "",
    "May we discuss your medical condition with any member of your family?":
      values.discussWithFamily === "yes"
        ? "Yes"
        : values.discussWithFamily === "no"
          ? "No"
          : "",
  };

  // Add conditional field if discussWithFamily is yes
  if (
    values.discussWithFamily === "yes" &&
    values.discussWithFamilyMemberName
  ) {
    questionAnswerData["If YES, please name the members allowed:"] =
      values.discussWithFamilyMemberName;
  }

  // Add signed by field
  if (values.allowedMembers) {
    questionAnswerData["This Consent Was Signed By"] = values.allowedMembers;
  }

  return (
    <Box
      id={"HIPAA Consent"}
      sx={{
        mt: 1,
      }}
    >
      <OutlineCard>
        <Box
          sx={{
            p: 2,
          }}
        >
          {/* Title */}
          <Typography
            variant="h5"
            align="center"
            gutterBottom
            sx={{
              fontWeight: "bold",
            }}
          >
            HIPAA Compliance Patient Consent Form
          </Typography>

          {/* Independent Text Content */}
          <Box
            sx={{
              mb: 2,
            }}
          >
            <Typography gutterBottom>
              Our Notice of Privacy Practices provides information about how we
              may use or disclose protected health information.
            </Typography>
            <Typography gutterBottom>
              The notice contains a patient's rights section describing your
              rights under the law. You ascertain that by your signature that
              you have reviewed our notice before signing this consent.
            </Typography>
            <Typography gutterBottom>
              The terms of the notice may change, if so, you will be notified at
              your next visit to update your signature/date.
            </Typography>
            <Typography gutterBottom>
              You have the right to restrict how your protected health
              information is used and disclosed for treatment, payment or
              healthcare operations. We are not required to agree with this
              restriction, but if we do, we shall honor this agreement. The
              HIPAA (Health Insurance Portability and Accountability Act of
              1996) law allows for the use of the information for treatment,
              payment, or healthcare operations.
            </Typography>
            <Typography gutterBottom>
              By signing this form, you consent to our use and disclosure of
              your protected healthcare information and potentially anonymous
              usage in a publication. You have the right to revoke this consent
              in writing, signed by you. However, such a revocation will not be
              retroactive.
            </Typography>
            <Typography gutterBottom>
              By signing this form, I understand that:
            </Typography>
            <ul style={{ marginLeft: 24, marginTop: 8, marginBottom: 8 }}>
              <li>
                Protected health information may be disclosed or used for
                treatment, payment, or healthcare operations.
              </li>
              <li>
                The practice reserves the right to change the privacy policy as
                allowed by law.
              </li>
              <li>
                The practice has the right to restrict the use of the
                information but the practice does not have to agree to those
                restrictions.
              </li>
              <li>
                The patient has the right to revoke this consent in writing at
                any time and all full disclosures will then cease.
              </li>
              <li>
                The practice may condition receipt of treatment upon execution
                of this consent.
              </li>
            </ul>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Questions and Answers */}
          {Object.keys(questionAnswerData).length > 0 && (
            <Box
              sx={{
                mb: 2,
              }}
            >
              <ProfileViewer
                data={questionAnswerData}
                withoutColn
                firstColW={8}
                secondColW={4}
              />
            </Box>
          )}

          {/* Signature Section */}
          {values.hippaConsentSign && (
            <Box
              sx={{
                mt: 2,
              }}
            >
              <Text mb={1} fontWeight="medium">
                Signature
              </Text>
              <img
                src={values.hippaConsentSign}
                alt="HIPAA Consent Signature"
                style={{
                  height: "150px",
                  width: "360px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                }}
              />
            </Box>
          )}
        </Box>
      </OutlineCard>
    </Box>
  );
};

export default PatientHippaConsent;
